import {
    styled, Box,
    Grid,
    Typography,
    Chip,
    Snackbar,
    Alert,
    Button,
} from '@mui/material';

import { TitledAppBar } from 'src/components/common/TitledAppBar';
import { ArrowBack, PlayArrow } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryRelevanceTestDefinition } from 'src/hooks/useQueryRelevanceTestDefinition';
import { useQueryRelevanceTestRuns } from 'src/hooks/useQueryRelevanceTestRuns';
import { useCreateRelevanceTestRun } from 'src/hooks/useCreateRelevanceTestRun';
import { useState, useEffect } from 'react';
import { mutateRelevanceTestDefinition } from 'src/mutations/mutateRelevanceTestDefinition';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDeleteRelevanceTestDefinition } from 'src/hooks/useDeleteRelevanceTestDefinition';
import { RELEVANCE_TEST_DEFINITION_SEARCH } from 'src/lib/routes';
import { TestRunResults } from './TestRunResults';
import { CurrentScoredEntities } from './CurrentScoredEntities';
import { SaveLoadingButton } from '../common/buttons/SaveLoadingButton';
import { CancelLoadingButton } from '../common/buttons/CancelLoadingButton';
import { SettingsMenu } from './SettingsMenu';
import { EmptyState } from '../common/EmptyState';
import { ButtonGroupWithPopper, ButtonGroupOptions } from '../common/ButtonGroupWithPopper';
import { isTestRunExperimental } from './utils';

const StyledContainer = styled(Box)(({ theme }) => ({
    padding: `0 ${theme.spacing(4)}`,
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
}));

const ActiveStyledChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    height: theme.spacing(6),
}));

const InactiveStyledChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    height: theme.spacing(6),
}));

export type PropTypes = {
    testDefinitionId: string;
};

export const RelevanceTestDefinitionDetails = (props: PropTypes): JSX.Element => {
    const { testDefinitionId } = props;
    const accessToken = useRecoilValue(isAuthorizedQuery) as string;
    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
    const navigate = useNavigate();
    const { data: testDefinition } = useQueryRelevanceTestDefinition(accessToken, testDefinitionId);

    const {
        data: testRuns,
    } = useQueryRelevanceTestRuns(accessToken, {
        testDefinitionId,
        offset: 0,
        limit: 1000,
    });

    const [scoredEntities, setScoredEntities] = useState(testDefinition?.scoredEntities || []);
    const [testRun, setTestRun] = useState(() => {
        if (!testRuns?.results?.length) {
            return undefined;
        }

        const firstNonExperimental = testRuns.results.find((run) => !isTestRunExperimental(run));

        return firstNonExperimental ?? testRuns.results[0];
    });
    const [kValue, setKValue] = useState(testDefinition?.kSampleSize ?? 50);
    const runTestMutation = useCreateRelevanceTestRun(testDefinitionId);
    const mutateDefinition = useMutation(mutateRelevanceTestDefinition);
    const deleteDefinition = useDeleteRelevanceTestDefinition({ useErrorBoundary: false });
    const [active, setActive] = useState(testDefinition?.isActive ?? true);
    const [hasChanges, setHasChanges] = useState(false);
    const [includeExperimental, setIncludeExperimental] = useState(false);
    const [runTestOptions, setRunTestOptions] = useState<ButtonGroupOptions>({
        searchBackend: 'prod',
        experimentFlags: {},
    });

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
            if (hasChanges) {
                const message = 'You have unsaved changes. Are you sure you want to leave?';

                e.preventDefault();
                e.returnValue = message;
                return message;
            }
            return undefined;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasChanges]);

    const handleRunTest = async (options: ButtonGroupOptions): Promise<void> => {
        const newTestRun = await runTestMutation.mutateAsync({
            accessToken,
            testDefinitionId,
            options,
        });

        // If an experimental test run was kicked off by the user,
        // show experimental test runs so the results are immediately visible
        if (isTestRunExperimental(newTestRun)) {
            setIncludeExperimental(true);
        }

        setTestRun(newTestRun);
        testRuns?.results.unshift(newTestRun);
    };

    const handleTestRunChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const selectedTestRun = testRuns?.results.find((run) => run.testRunId === event.target.value);

        setTestRun(selectedTestRun);
    };

    const markChanged = (): void => setHasChanges(true);

    const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setActive(event.target.checked);
        markChanged();
    };

    const handleDelete = async (): Promise<void> => {
        await deleteDefinition.mutateAsync({
            accessToken,
            testDefinitionId,
        });

        navigate(RELEVANCE_TEST_DEFINITION_SEARCH);
    };
    const handleCancel = (): void => {
        setScoredEntities(testDefinition?.scoredEntities || []);
        setKValue(testDefinition?.kSampleSize || 50);
        setActive(testDefinition?.isActive ?? true);
        setHasChanges(false);
    };
    const handleSave = async (): Promise<void> => {
        await mutateDefinition.mutateAsync({
            accessToken,
            testDefinitionId,
            data: {
                ...testDefinition,
                scoredEntities,
                kSampleSize: kValue,
                isActive: active,
            } as Models.IngestConfigApi.RelevanceTests.UpdateRelevanceTestDefinition,
        });
        setHasChanges(false);
        setShowSuccessSnackbar(true);
    };

    const saveUpdatedEntity = (
        entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
    ): void => {
        const selectedEntity = scoredEntities?.find((e) => e.designConceptId === entity.designConceptId);

        if (selectedEntity) {
            const newScoredEntities = scoredEntities?.map((e) => {
                if (e.designConceptId === selectedEntity.designConceptId) {
                    return entity;
                }
                return e;
            });

            setScoredEntities(newScoredEntities);
        } else {
            const newScoredEntities = [
                ...(scoredEntities || []),
                entity,
            ];

            setScoredEntities(newScoredEntities);
        }
        markChanged();
    };

    const handleScoredEntityChange = (
        entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        score: Models.IngestConfigApi.RelevanceTests.RelevanceScore,
    ): void => {
        const updatedEntity = {
            ...entity,
            relevance: score,
        };

        saveUpdatedEntity(updatedEntity);
    };

    const handleCommentChange = (
        entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        comment: string,
    ): void => {
        const updatedEntity = {
            ...entity,
            reason: comment,
        };

        saveUpdatedEntity(updatedEntity);
    };

    const handleEntityAdded = (entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity): void => {
        // Add the new entity to the list
        setScoredEntities((prev) => [entity, ...prev]);
        markChanged();
    };

    const handleNavigation = (path: string): void => {
        if (hasChanges) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');

            if (confirmed) {
                navigate(path);
            }
        } else {
            navigate(path);
        }
    };

    const handleIncludeExperimentalChange = (include: boolean): void => {
        setIncludeExperimental(include);

        if (!include && testRuns?.results) {
            const latestNonExperimental = testRuns.results.find((run) => !isTestRunExperimental(run));

            if (latestNonExperimental) {
                setTestRun(latestNonExperimental);
            }
        }
    };

    const getRunTestButtonText = (options: ButtonGroupOptions): JSX.Element => {
        const experimentFlagCount = Object.values(options.experimentFlags || {}).length;
        const isBackendChanged = options.searchBackend !== 'prod';

        return (
            <>
                Run New Test
                {(isBackendChanged || experimentFlagCount > 0) && (
                    <Typography
                        fontStyle="normal"
                        sx={{
                            textTransform: 'none',
                            pl: 1,
                        }}
                        variant="caption"
                    >
                        (
                        {options.searchBackend}
                        {`${experimentFlagCount > 0 ? `, ${experimentFlagCount} ${experimentFlagCount > 1 ? 'flags' : 'flag'}` : ''}`}
                        )
                    </Typography>
                )}
            </>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                position: 'relative',
                overflow: 'auto',
                gap: 4,
            }}
        >
            <TitledAppBar title="Test Definition Details">
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <SettingsMenu
                        active={active}
                        kValue={kValue}
                        onDelete={handleDelete}
                        onKValueChange={(value: number): void => { setKValue(value); markChanged(); }}
                        onToggleActive={handleActiveChange}
                    />
                    <ButtonGroupWithPopper
                        buttonIcon={<PlayArrow />}
                        buttonText={getRunTestButtonText(runTestOptions)}
                        tooltipText="Run a new test with current settings"
                        onButtonClick={handleRunTest}
                        onOptionsChange={setRunTestOptions}
                    />
                </Box>
            </TitledAppBar>
            <StyledContainer>
                <Button
                    color="secondary"
                    size="small"
                    startIcon={<ArrowBack />}
                    sx={{
                        justifySelf: 'flex-start',
                        width: 'fit-content',
                        mb: -2,
                    }}
                    variant="text"
                    onClick={(): void => handleNavigation(RELEVANCE_TEST_DEFINITION_SEARCH)}
                >
                    Back to Test Definitions
                </Button>
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    borderBottom: 1,
                    borderColor: 'divider',
                    pb: 2,
                }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h4">{testDefinition?.keywordSearchTerm}</Typography>
                            {active ? (
                                <ActiveStyledChip label="Active" />
                            ) : (
                                <InactiveStyledChip label="Inactive" />
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body1">
                                {`Gallery: ${testDefinition?.productName} (${testDefinition?.productKey}) | Culture: ${testDefinition?.culture} | `}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography mr={1} variant="body1">K Value:</Typography>
                                <Typography variant="body1">{kValue}</Typography>
                            </Box>
                            {testDefinition?.createdBy && (
                                <Typography variant="body1">
                                    {`| Created By: ${testDefinition?.createdBy.split('@')[0]}`}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <CancelLoadingButton disabled={!hasChanges} onClick={handleCancel}>
                            Cancel
                        </CancelLoadingButton>
                        <SaveLoadingButton
                            disabled={!hasChanges}
                            variant="contained"
                            onClick={handleSave}
                        >
                            Save Changes
                        </SaveLoadingButton>
                    </Box>
                </Box>
                <Grid container spacing={4}>
                    <Grid item xs={6}>
                        {testDefinition && (
                            <CurrentScoredEntities
                                culture={testDefinition.culture}
                                handleCommentChange={handleCommentChange}
                                handleScoredEntityChange={handleScoredEntityChange}
                                markChanged={markChanged}
                                productKey={testDefinition.productKey}
                                scoredEntities={scoredEntities}
                                onEntityAdded={handleEntityAdded}
                            />
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        {testRun && testDefinition ? (
                            <TestRunResults
                                handleCommentChange={handleCommentChange}
                                handleScoredEntityChange={handleScoredEntityChange}
                                handleTestRunChange={handleTestRunChange}
                                includeExperimental={includeExperimental}
                                kValue={kValue}
                                productKey={testDefinition.productKey}
                                scoredEntities={scoredEntities}
                                testRun={testRun}
                                testRuns={testRuns}
                                onIncludeExperimentalChange={handleIncludeExperimentalChange}
                            />
                        ) : (
                            <EmptyState sx={{ p: 4 }}>
                                <Typography gutterBottom variant="h6">
                                    No Test Runs
                                </Typography>
                                <Typography variant="body1">
                                    Click the Run New Test button to run a test.
                                </Typography>
                            </EmptyState>
                        )}
                    </Grid>
                </Grid>
            </StyledContainer>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={3000}
                open={showSuccessSnackbar}
                onClose={(): void => setShowSuccessSnackbar(false)}
            >
                <Alert
                    severity="success"
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    Test definition saved successfully
                </Alert>
            </Snackbar>
        </Box>
    );
};
